import React, { useState } from 'react';
import {
  useCart,
  useCartCount,
  useCartItems,
  useCheckoutUrl,
  useUpdateItemQuantity,
} from 'gatsby-theme-shopify-manager';
import { Link } from 'gatsby';
import PropTypes from 'prop-types';

import { HeadingWithLink } from './heading-with-link';
import { TableHeading, TableBody, TableCell } from './table-elements';
import { useGraphQL } from '../hooks';

function CartContent() {
  const cartCount = useCartCount();
  const cartItems = useCartItems();

  function EmptyCart() {
    return (
      <HeadingWithLink
        heading="Shopping bag is empty"
        copy="You have no items in your shopping bag"
        linkText="Continue shopping"
        slug="/"
      />
    );
  }

  if (cartItems.length < 1) {
    return <EmptyCart />;
  }

  return (
    <article className="lg:px-8">
      <h2 className="px-4 tracking-wider uppercase sm:px-6 lg:px-0">
        My Bag ({cartCount} Items)
      </h2>
      <div className="grid gap-4 lg:grid-cols-3">
        <LineItems cartItems={cartItems} />
        <Total />
      </div>
    </article>
  );
}

function LineItems({ cartItems }) {
  return (
    <div className="px-4 overflow-hidden lg:col-span-2 sm:px-6 lg:px-0">
      <div className="flex flex-col">
        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden">
              <table className="min-w-full">
                <thead>
                  <tr>
                    <TableHeading className="text-left sm:text-center">
                      Item
                    </TableHeading>
                    <TableHeading className="hidden text-left sm:table-cell">
                      Quantity
                    </TableHeading>
                    <TableHeading className="text-right">Price</TableHeading>
                    <TableHeading className="hidden sm:table-cell">
                      <span className="sr-only">Remove</span>
                    </TableHeading>
                  </tr>
                </thead>
                <TableBody>
                  {cartItems.map((lineItem) => (
                    <LineItem key={lineItem.variant.id} lineItem={lineItem} />
                  ))}
                </TableBody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

LineItems.propTypes = {
  cartItems: PropTypes.array.isRequired,
};

function LineItem({ lineItem }) {
  const [quantity, setQuantity] = useState(lineItem.quantity);
  const updateItemQuantity = useUpdateItemQuantity();

  const { placeholderImage } = useGraphQL();

  async function handleChange() {
    if (lineItem === null) {
      return;
    }
    try {
      await updateItemQuantity(lineItem.variant.id, Number(quantity));
    } catch (error) {
      console.error(error);
    }
  }

  function handleBlur() {
    if (quantity === '') setQuantity(1);
    handleChange();
  }

  function RemoveLineItem() {
    return (
      <button
        type="button"
        onClick={() => updateItemQuantity(lineItem.variant.id, 0)}
        className="p-2 rounded focus:shadow-none focus:bg-gray-100 active:bg-gray-100"
      >
        <svg
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          className="w-5 h-5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    );
  }

  return (
    <tr>
      <TableCell>
        <div className="flex flex-wrap -ml-6">
          <div className="flex-shrink-0 w-48 mb-4 mr-4">
            <Link
              aria-hidden
              tabIndex={-1}
              to={`/products/${lineItem.variant.product.handle}`}
            >
              <div className="relative h-0 aspect-ratio-square">
                <img
                  className="absolute inset-0"
                  src={
                    lineItem.variant.image?.src || placeholderImage.publicURL
                  }
                  alt={lineItem.variant.image?.altText || ''}
                />
              </div>
            </Link>
          </div>
          <div className="space-y-3 text-sm leading-5 tracking-wider uppercase">
            <Link
              to={`/products/${lineItem.variant.product.handle}`}
              className="inline-flex"
            >
              <div className="font-medium text-dark-pink focus:underline">
                {lineItem.title}
              </div>
            </Link>
            {lineItem.variant.selectedOptions.map((option) => (
              <div key={option.name} className="font-medium text-dark-pink">
                {option.name}: {option.value}
              </div>
            ))}
          </div>
        </div>
      </TableCell>
      <TableCell className="hidden sm:table-cell">
        <label htmlFor="cart_qty">
          <span className="sr-only">Quantity</span>
          <input
            id="cart_qty"
            type="number"
            onChange={(e) => setQuantity(e.target.value)}
            onBlur={handleBlur}
            value={quantity}
            min={0}
            pattern="[0-9]*"
            className="w-16 text-sm font-medium leading-5 tracking-wider rounded-none border-light-pink form-input focus:border-light-pink"
          />
        </label>
      </TableCell>
      <TableCell className="pr-0 sm:pr-6">
        <div className="space-y-3 text-sm leading-5 tracking-wider text-right uppercase tabular-nums">
          <div>
            <div className="font-medium text-dark-pink">
              ${lineItem.variant.price}
            </div>
          </div>
          <div className="sm:hidden">
            <label htmlFor="cart_qty">
              <span className="sr-only">Quantity</span>
              <input
                id="cart_qty"
                type="number"
                onChange={(e) => setQuantity(e.target.value)}
                onBlur={handleBlur}
                value={quantity}
                min={0}
                pattern="[0-9]*"
                className="w-16 text-sm font-medium leading-5 tracking-wider rounded-none border-light-pink form-input focus:border-light-pink"
              />
            </label>
            <div className="sm:hidden">
              <RemoveLineItem />
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell className="hidden sm:table-cell">
        <div className="flex justify-end -mt-2">
          <RemoveLineItem />
        </div>
      </TableCell>
    </tr>
  );
}

LineItem.propTypes = {
  lineItem: PropTypes.object.isRequired,
};

function Total() {
  const cart = useCart();
  const cartItems = useCartItems();
  const checkoutUrl = useCheckoutUrl();
  return (
    <article className="w-full max-w-2xl px-4 mx-auto sm:px-6 lg:p-0">
      <div className="px-4 py-6 bg-light-gray">
        <div className="w-full mx-auto space-y-4 tracking-wider text-gray-700 uppercase sm:max-w-sm">
          <h2 className="font-medium">Total</h2>
          <dl className="min-w-full text-left">
            {cartItems.map((cartItem) => (
              <div
                key={cartItem.variant.id}
                className="flex justify-between mt-4 space-x-4"
              >
                <dt className="font-medium">
                  {cartItem.title}{' '}
                  <span className="inline-block ml-1 font-normal lowercase">
                    &times;{cartItem.quantity}
                  </span>
                </dt>
                <dd className="font-normal text-right tabular-nums">
                  $
                  {(cartItem.variant.price.split('.').join('') *
                    cartItem.quantity) /
                    100}
                </dd>
              </div>
            ))}
            <div className="flex justify-between mt-4 space-x-4">
              <dt className="text-xl font-medium">Order Total</dt>
              <dd className="text-xl font-medium tabular-nums">
                ${Number(cart.totalPrice).toFixed(2)}
              </dd>
            </div>
          </dl>
          <a href={checkoutUrl} className="w-full uppercase button">
            Check out
          </a>
          <Link to="/" className="w-full uppercase button button-inverted">
            Continue Shopping
          </Link>
        </div>
      </div>
    </article>
  );
}

export { CartContent };
